import { useState, useMemo, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, X, Loader2, Video, AlertTriangle, ShieldAlert } from "lucide-react";
import ExerciseVideoUploader from "@/components/ExerciseVideoUploader";

/** Inline exercise creator — reuses the same fields as the Library form and the
 *  existing secure ExerciseVideoUploader so a missing exercise can be created
 *  without leaving the Programme Roadmap editor. */
function InlineExerciseCreator({ onCreated, onClose }) {
  const [form, setForm] = useState({
    name: "", category: "Lower body", description: "", body_region: "",
    difficulty: "Beginner", phase: "Rehabilitation", equipment: "", instructions: "",
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [matches, setMatches] = useState([]);
  const [checking, setChecking] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  // Advisory duplicate check (debounced) — surfaces canonical matches so a
  // clinician doesn't create a near-duplicate DRAFT. Never blocks a genuinely
  // distinct exercise; the exact-name guard is enforced again server-side on save.
  useEffect(() => {
    const name = form.name.trim();
    if (created || name.length < 3) { setMatches([]); return undefined; }
    let alive = true;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/exercises/duplicate-check", {
          params: { name, body_region: form.body_region || undefined, equipment: form.equipment || undefined },
        });
        if (alive) setMatches(r.data?.matches || []);
      } catch { if (alive) setMatches([]); }
      finally { if (alive) setChecking(false); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [form.name, form.body_region, form.equipment, created]);

  const exactMatch = matches.find((m) => m.exact);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.description.trim()) return toast.error("Description is required");
    setSaving(true);
    try {
      const r = await api.post("/exercises", form, { params: { enforce_unique: true } });
      setCreated(r.data);
      toast.success(`Draft "${r.data.name}" created — pending clinical review`);
      onCreated?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === "string" ? d : null))) || "Could not create exercise");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-start justify-center p-3" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(3px)" }} onClick={onClose} data-testid="inline-exercise-creator">
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} className="fl-card fl-card-elevated w-full max-w-lg my-4 max-h-[92vh] overflow-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="fl-heading text-lg">Create new exercise</div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white" data-testid="inline-ex-close"><X size={18} /></button>
        </div>
        {!created ? (
          <>
            <input className="fl-input" placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="inline-ex-name" />
            <div className="grid grid-cols-2 gap-2">
              <input className="fl-input" placeholder="Category" value={form.category} onChange={(e) => set("category", e.target.value)} />
              <input className="fl-input" placeholder="Body region" value={form.body_region} onChange={(e) => set("body_region", e.target.value)} />
              <select className="fl-input" value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
                {["Beginner", "Intermediate", "Advanced"].map((d) => <option key={d}>{d}</option>)}
              </select>
              <input className="fl-input" placeholder="Equipment" value={form.equipment} onChange={(e) => set("equipment", e.target.value)} />
            </div>
            <textarea className="fl-input" rows={2} placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="inline-ex-description" />
            <textarea className="fl-input" rows={2} placeholder="Instructions (optional)" value={form.instructions} onChange={(e) => set("instructions", e.target.value)} />

            {checking && (
              <div className="text-[11px] text-zinc-500 flex items-center gap-1.5" data-testid="inline-ex-checking">
                <Loader2 size={11} className="animate-spin" /> Checking the library for existing exercises…
              </div>
            )}
            {matches.length > 0 && (
              <div className={`rounded-lg border p-2.5 space-y-1.5 ${exactMatch ? "border-red-400/40 bg-red-400/10" : "border-amber-400/30 bg-amber-400/10"}`} data-testid="inline-ex-duplicates">
                <div className={`text-[11px] font-semibold flex items-center gap-1.5 ${exactMatch ? "text-red-200" : "text-amber-200"}`}>
                  <AlertTriangle size={12} />
                  {exactMatch
                    ? "An exercise with this exact name already exists"
                    : `Possible existing ${matches.length === 1 ? "match" : "matches"} — avoid creating a duplicate`}
                </div>
                <ul className="text-[11px] space-y-1">
                  {matches.map((m) => (
                    <li key={m.id} className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="text-left text-zinc-100 underline decoration-dotted underline-offset-2 hover:text-[#f47a20]"
                        onClick={() => onCreated?.(m)}
                        data-testid={`inline-ex-use-${m.id}`}
                      >
                        Use “{m.name}”
                      </button>
                      <span className="text-zinc-500">
                        {m.body_region ? `· ${m.body_region}` : ""}
                        {m.exact ? " · exact name" : ""}
                        {m.clinical_review_status !== "approved" ? " · pending" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 p-2.5 text-[11px] text-zinc-300" data-testid="inline-ex-draft-notice">
              <ShieldAlert size={13} className="shrink-0 mt-0.5 text-[#f47a20]" />
              <span>Saved as an inactive <b className="text-[#f47a20]">DRAFT</b> (pending clinical review). Draft exercises are never shown to patients or used by the programme generator until a clinician approves and activates them. Video is optional.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="fl-btn-ghost text-xs" onClick={onClose}>Cancel</button>
              <button type="button" className="fl-btn text-xs" onClick={save} disabled={saving || !!exactMatch} title={exactMatch ? "Use the existing exercise instead" : undefined} data-testid="inline-ex-save">{saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create draft & select</button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-emerald-400" data-testid="inline-ex-created">Draft “{created.name}” created and selected — pending clinical review.</div>
            <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 p-2.5 text-[11px] text-zinc-400">
              <ShieldAlert size={13} className="shrink-0 mt-0.5 text-[#f47a20]" />
              <span>This exercise stays an inactive DRAFT until a clinician approves and activates it in the Library. It won’t reach patients before then.</span>
            </div>
            <div className="flex gap-2">
              <button type="button" className="fl-btn-ghost text-xs" onClick={() => setShowVideo(true)} data-testid="inline-ex-add-video"><Video size={12} /> Upload demo video (optional)</button>
              <button type="button" className="fl-btn text-xs ml-auto" onClick={onClose}>Done</button>
            </div>
            {showVideo && <ExerciseVideoUploader exercise={created} onDone={(doc) => setCreated(doc)} onClose={() => setShowVideo(false)} />}
          </div>
        )}
      </div>
    </div>
  );
}

/** Searchable exercise selector against the approved library. */
export function ExercisePicker({ value, library, onSelect, onLibraryChange, readOnly, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const selected = library.find((x) => x.id === value);
  const draftOf = (x) => !!x && (x.active === false || (x.clinical_review_status && x.clinical_review_status !== "approved"));
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return library.slice(0, 40);
    return library.filter((x) =>
      [x.name, x.body_region, x.equipment, x.category, x.target].filter(Boolean).join(" ").toLowerCase().includes(s)
    ).slice(0, 40);
  }, [q, library]);

  if (readOnly) return <div className="text-xs text-zinc-300">{selected?.name || "— no exercise —"}</div>;

  return (
    <div className="relative" data-testid={testid}>
      <button type="button" className="fl-input text-xs w-full text-left flex items-center gap-2" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}>
        <Search size={12} className="text-zinc-500" />
        <span className={selected ? "text-zinc-100" : "text-zinc-500"}>{selected?.name || "Choose an exercise from the library…"}</span>
        {selected && draftOf(selected) && (
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#f47a20]/15 text-[#f47a20] border border-[#f47a20]/40" data-testid={`${testid}-draft-badge`}>DRAFT</span>
        )}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-950 p-2 shadow-xl max-h-72 overflow-auto">
          <input autoFocus className="fl-input text-xs mb-2" placeholder="Search name / region / equipment…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
          <div className="space-y-0.5">
            {filtered.map((x) => (
              <button key={x.id} type="button" className="w-full text-left px-2 py-1 rounded text-xs text-zinc-200 hover:bg-zinc-800 flex items-center gap-1" onClick={() => { onSelect(x.id); setOpen(false); setQ(""); }} data-testid={`${testid}-opt-${x.id}`}>
                <span>{x.name} <span className="text-zinc-500">{x.body_region ? `· ${x.body_region}` : ""}</span></span>
                {draftOf(x) && <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-[#f47a20]/15 text-[#f47a20] border border-[#f47a20]/40">DRAFT</span>}
              </button>
            ))}
            {!filtered.length && <div className="text-xs text-zinc-500 px-2 py-1">No matches.</div>}
          </div>
          <button type="button" className="fl-btn-ghost text-xs mt-2 w-full justify-center" onClick={() => { setCreating(true); setOpen(false); }} data-testid={`${testid}-create-new`}><Plus size={12} /> Create new exercise</button>
        </div>
      )}
      {creating && <InlineExerciseCreator onClose={() => setCreating(false)} onCreated={(doc) => { onLibraryChange?.(doc); onSelect(doc.id); setCreating(false); }} />}
    </div>
  );
}
